import { merge } from "lodash";
import { compact, isEmpty, last, reject, toUpper, trim } from "lodash/fp";
import { DateTime } from "luxon";

import type { User, UserMetadata } from "@app/common/types";
import Config from "@app/config";
import { parseUTCDateTime } from "@app/utils/utils";

// e.g. pronouns "Jane Doe (She/Her)"
export const PARENTHESES_VALUE_REGEX = / [([{]([^)\]}]+)[)\]}]/g;

export const getDomainFromEmail = (email: string) => email.replace(/.+?@/, "");

export const getInitials = (name: string) => {
  if (!name || isEmpty(trim(name))) return;

  const sanitizedName = name.replace(PARENTHESES_VALUE_REGEX, "");

  // ignore any names that are just numbers
  let nameParts: string[] = reject(
    (val) => Boolean(val.match(/^\d+$/)),
    sanitizedName.split(/\s/)
  );
  if (isEmpty(nameParts)) {
    // the only way this would be true is if all their name parts are only
    // numbers, so just re-split w/o the filter
    nameParts = sanitizedName.split(/\s/);
  }

  let initials = nameParts[0].charAt(0);
  if (nameParts.length > 1) {
    initials += last(nameParts)!.charAt(0);
  }
  return toUpper(initials);
};

export const getUserFullName = (user: {
  firstName: string;
  lastName: string;
}) => compact([user.firstName, user.lastName]).join(" ").trim();

export const populateUser = <
  T extends {
    firstName: string;
    lastName: string;
    createdOn?: string | number;
  },
>(
  user: T
) => {
  const fullName = getUserFullName(user);
  return {
    ...user,
    fullName,
    createdOn: user.createdOn
      ? typeof user.createdOn === "number"
        ? user.createdOn
        : parseUTCDateTime(user.createdOn).toMillis()
      : null,
    initials: getInitials(fullName)!,
  };
};

export const combineUserMetadata = (
  user: { userMetadata?: UserMetadata },
  setting: Partial<UserMetadata>
) => {
  const userMetadata: UserMetadata = merge(
    {},
    user.userMetadata || {},
    setting
  );
  return { userMetadata };
};

export const hasCompletedOnboarding = (user: User | null) =>
  Boolean(user?.userMetadata?.onboardingComplete);

export const getSchedulingLinkUrl = (
  userVanityId: string,
  linkVanityId?: string
) =>
  `${Config.RSS_LINKS_URL}/${userVanityId}${
    linkVanityId ? `/${linkVanityId}` : ""
  }`;

export const hasCompanyEmail = (user: User | null) =>
  Boolean(user?.isCompanyEmail);

export const getEmailDomain = (user: { email: string } | null) =>
  user?.email ? getDomainFromEmail(user.email) : "";

export const createdBefore = (user: User | null, date: DateTime) => {
  if (!user?.createdOn) return false;
  return user.createdOn < date.toMillis();
};

export const createdAfter = (user: User | null, date: DateTime) => {
  if (!user?.createdOn) return false;
  return user.createdOn > date.toMillis();
};

/** Whether the user was created recently (within the last two weeks) */
export const isRecentUser = (user: User | null) => {
  const twoWeeksAgo = DateTime.now().minus({ weeks: 2 });
  return createdAfter(user, twoWeeksAgo);
};

export const requireSettingsConfirmation = (user: User | null) => {
  if (
    user?.userMetadata?.initialSettingsConfirmationState !==
    "pending_confirmation"
  ) {
    return false;
  }

  return isRecentUser(user);
};

export const eligibleForFirstTimeSettingsBanner = (user: User | null) => {
  if (!user) return false;

  // only show the first time
  return (
    isRecentUser(user) &&
    user.userMetadata?.signupSource === "share" &&
    user.userMetadata?.initialSettingsConfirmationState == null
  );
};
